import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const parseHashParams = (hash: string) => {
  const normalizedHash = hash.startsWith('#') ? hash.slice(1) : hash;
  return new URLSearchParams(normalizedHash);
};

const getSafeRedirectPath = (value: string | null) => {
  if (!value || !value.startsWith('/')) {
    return '/';
  }

  return value;
};

const AuthCallbackPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);

  const redirectPath = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return getSafeRedirectPath(searchParams.get('next'));
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    const completeCallback = async () => {
      const searchParams = new URLSearchParams(location.search);
      const hashParams = parseHashParams(location.hash);
      const code = searchParams.get('code');
      const tokenHash = searchParams.get('token_hash');
      const otpType = searchParams.get('type');
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const authError = searchParams.get('error_description') || hashParams.get('error_description');

      if (authError) {
        if (!cancelled) {
          setError(authError);
          setProcessing(false);
        }
        return;
      }

      try {
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            throw error;
          }
        } else if (tokenHash && otpType) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: otpType as any,
          });
          if (error) {
            throw error;
          }
        } else if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            throw error;
          }
        } else {
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            throw new Error('No authentication result was found in the callback URL.');
          }
        }

        if (!cancelled) {
          navigate(redirectPath, { replace: true });
        }
      } catch (callbackError) {
        const message =
          callbackError instanceof Error
            ? callbackError.message
            : typeof callbackError === 'object' && callbackError !== null && 'message' in callbackError
              ? String((callbackError as { message: unknown }).message)
              : 'We could not complete the sign-in callback.'

        if (!cancelled) {
          setError(message);
          setProcessing(false);
        }
        return;
      }

      if (!cancelled) {
        setProcessing(false);
      }
    };

    void completeCallback();

    return () => {
      cancelled = true;
    };
  }, [location.hash, location.search, navigate, redirectPath]);

  if (processing && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <h1 className="mt-6 text-xl font-semibold text-gray-900">Completing sign-in</h1>
          <p className="mt-2 text-sm text-gray-600">
            We&apos;re finalizing your secure session now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-8 shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">Authentication issue</h1>
        <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error || 'We could not complete the authentication callback.'}
        </p>
        <button
          className="mt-6 w-full rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-900"
          onClick={() => navigate('/auth', { replace: true })}
          type="button"
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
